import { formatDuration, intervalToDuration } from "date-fns";

import { useChannel } from "@/contexts/ChannelContext";
import Typography from "@components/Typography";

import styles from "./ChannelStatistics.module.css";

function getDurationStringFromSeconds(seconds: number) {
  // Convert the total seconds to a duration object
  const milliseconds = seconds * 1000;
  const duration = intervalToDuration({ start: 0, end: milliseconds });

  // Don't display seconds if the duration is more than 2 minutes
  const twoMinutesInSeconds = 60 * 2;
  if (seconds > twoMinutesInSeconds) {
    duration.seconds = 0;
  }

  // Format the duration
  let durationString = formatDuration(duration);
  durationString = durationString
    .replace(/hours?/g, "h")
    .replace(/minutes?/g, "min")
    .replace(/seconds?/g, "s");
  return durationString;
}

const ChannelStatistics = () => {
  const { channel: channel } = useChannel();

  const internal_statistics = channel?.internal_statistics;

  return (
    <div className={styles.channelStatistics}>
      <Typography variant="subtitle-2" className={styles.title}>
        Statistics for the last 30 days
      </Typography>
      <div>
        {internal_statistics?.published_hours != undefined && (
          <>
            <div className={styles.statsRow}>
              <span className={styles.statsText}>Published hours</span>
              <span className={styles.statsNumber}>
                {internal_statistics?.published_hours} h
              </span>
            </div>
            <hr className={styles.divider} />
          </>
        )}

        {internal_statistics?.transcribed_hours != undefined && (
          <>
            <div className={styles.statsRow}>
              <span className={styles.statsText}>Transcribed hours</span>
              <span className={styles.statsNumber}>
                {internal_statistics?.transcribed_hours} h
              </span>
            </div>
            <hr className={styles.divider} />
          </>
        )}

        {internal_statistics?.average_episode_length_as_seconds !=
          undefined && (
          <>
            <div className={styles.statsRow}>
              <span className={styles.statsText}>Average episode length</span>
              <span className={styles.statsNumber}>
                {internal_statistics?.average_episode_length_as_seconds &&
                  getDurationStringFromSeconds(
                    internal_statistics?.average_episode_length_as_seconds
                  )}
              </span>
            </div>
            <hr className={styles.divider} />
          </>
        )}

        {internal_statistics?.num_episodes != undefined && (
          <>
            <div className={styles.statsRow}>
              <span className={styles.statsText}>Number of episodes</span>
              <span className={styles.statsNumber}>
                {internal_statistics?.num_episodes}
              </span>
            </div>
            <hr className={styles.divider} />
          </>
        )}

        {internal_statistics?.num_mentions != undefined && (
          <>
            <div className={styles.statsRow}>
              <span className={styles.statsText}>Number of mentions</span>
              <span className={styles.statsNumber}>
                {internal_statistics?.num_mentions}
              </span>
            </div>
            <hr className={styles.divider} />
          </>
        )}

        {internal_statistics?.num_mentions_per_transcribed_hour !=
          undefined && (
          <>
            <div className={styles.statsRow}>
              <span className={styles.statsText}>
                Number of mentions per transcribed hour
              </span>
              <span className={styles.statsNumber}>
                {internal_statistics?.num_mentions_per_transcribed_hour}
              </span>
            </div>
            <hr className={styles.divider} />
          </>
        )}

        {internal_statistics?.transcription_cost != undefined && (
          <>
            <div className={styles.statsRow}>
              <span className={styles.statsText}>
                Estimated total transcription cost
              </span>
              <span className={styles.statsNumber}>
                {internal_statistics?.transcription_cost} kr
              </span>
            </div>
            <hr className={styles.divider} />
          </>
        )}

        {internal_statistics?.cost_per_mention != undefined && (
          <>
            <div className={styles.statsRow}>
              <span className={styles.statsText}>
                Estimated cost per mention
              </span>
              <span className={styles.statsNumber}>
                {internal_statistics?.cost_per_mention} kr
              </span>
            </div>
            <hr className={styles.divider} />
          </>
        )}
      </div>
    </div>
  );
};

export default ChannelStatistics;
